import NiceModal from '@ebay/nice-modal-react';
import { Badge, Button, Group, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconPlug, IconRefresh } from '@tabler/icons-react';

import { useDisconnectPage } from '../../../../api';
import { useIgConnectOAuth } from '../../../../hooks';
import { InstPage } from '../../../../types';

import { ConfirmModal } from '@/components/Modals/Confirm';

const DAY_MS = 24 * 60 * 60 * 1000;
const EXPIRY_WARNING_DAYS = 7;

/**
 * Shows the Instagram Login (instagram-connect) token health and exposes the
 * reconnect (OAuth) and disconnect (token revocation) actions. Renders nothing
 * for non IG-Login pages.
 */
export const IgTokenStatus = ({ page }: { page: InstPage }) => {
  const { open, isLoading: isConnecting } = useIgConnectOAuth({ department: page.department });
  const disconnect = useDisconnectPage();

  if (page.integration !== 'instagram-connect') {
    return null;
  }

  const expiresAt = page.inst.igTokenExpiresAt;
  const now = Date.now();
  const isArchived = page.config.isArchived;
  const isRestoring = page.config.isRestoring;
  // Archiving sets isConnected=false without touching the token, so archived
  // pages are a neutral state, not a token-health problem — exclude them to
  // avoid a false "reconnect" badge (and an OAuth that wouldn't un-archive).
  const needsReconnect =
    !isArchived && (!page.config.isConnected || !expiresAt || expiresAt <= now);
  const daysLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - now) / DAY_MS)) : 0;
  const isExpiring = !needsReconnect && !isArchived && daysLeft <= EXPIRY_WARNING_DAYS;

  const reconnectButton = (
    <Button
      size="compact-xs"
      variant="light"
      color="red"
      leftSection={<IconRefresh size={14} />}
      loading={isConnecting}
      onClick={open}
    >
      Переподключить
    </Button>
  );

  const disconnectButton = isRestoring ? (
    <Tooltip label="Нельзя отключить во время восстановления из архива">
      <Button
        size="compact-xs"
        variant="light"
        color="gray"
        leftSection={<IconPlug size={14} />}
        disabled
      >
        Отключить
      </Button>
    </Tooltip>
  ) : (
    <Tooltip label="Отключить страницу и отозвать токен">
      <Button
        size="compact-xs"
        variant="light"
        color="gray"
        leftSection={<IconPlug size={14} />}
        loading={disconnect.isLoading}
        onClick={() =>
          NiceModal.show(ConfirmModal, {
            title: `Отключить страницу (${page.name})? Токен Instagram будет отозван.`,
            onSuccess: () => disconnect.mutate({ pageId: page._id, platform: 'instagram' }),
          })
        }
      >
        Отключить
      </Button>
    </Tooltip>
  );

  // Archived pages: neutral, no token-health badge or actions.
  if (isArchived) {
    return null;
  }

  if (needsReconnect) {
    return (
      <Group gap={6} align="center">
        <Tooltip label="Токен истёк или отозван — требуется переподключение">
          <Badge color="red" size="sm" leftSection={<IconAlertTriangle size={12} />}>
            Требуется переподключение
          </Badge>
        </Tooltip>
        {reconnectButton}
        {/* A connected-but-expired page must still be disconnectable (AC1). */}
        {page.config.isConnected && disconnectButton}
      </Group>
    );
  }

  return (
    <Group gap={6} align="center">
      {isExpiring ? (
        <Tooltip label="Токен скоро истечёт — рекомендуется переподключение">
          <Badge color="orange" size="sm" leftSection={<IconAlertTriangle size={12} />}>
            Истекает через {daysLeft} дн.
          </Badge>
        </Tooltip>
      ) : (
        <Tooltip label="Токен Instagram Login активен">
          <Badge color="teal" size="sm">
            Активен · {daysLeft} дн.
          </Badge>
        </Tooltip>
      )}
      {isExpiring && reconnectButton}
      {disconnectButton}
    </Group>
  );
};
